import { Check, ChevronRight } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { OrgLink } from '@/components/shared/OrgLink'
import type { SetupStep } from '../types/setup-progress.types'

interface SetupProgressItemProps {
  step: SetupStep
  label: string
  description: string
  icon: LucideIcon
}

export function SetupProgressItem({ step, description, icon: Icon }: SetupProgressItemProps) {
  const content = (
    <div className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg group transition-smooth">
      {/* Icon */}
      <div
        className={`flex items-center justify-center h-7 w-7 rounded-md shrink-0 transition-smooth ${
          step.isComplete
            ? 'bg-success-100 text-success-600'
            : 'bg-primary-50 text-primary-500'
        }`}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>

      {/* Description & progress */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-xs leading-tight transition-smooth ${
            step.isComplete
              ? 'text-neutral-400 line-through decoration-neutral-300'
              : 'text-neutral-900 font-medium'
          }`}
        >
          {description}
        </p>

        {/* Mini progress bar */}
        {!step.isComplete && (
          <div className="h-[3px] w-full bg-primary-100 rounded-full mt-1.5 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-400"
              initial={{ width: '0%' }}
              animate={{ width: `${step.completionRatio * 100}%` }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            />
          </div>
        )}
      </div>

      {/* Status indicator */}
      <div className="shrink-0">
        {step.isComplete ? (
          <div className="flex items-center justify-center h-4 w-4 rounded-full bg-success-500">
            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
          </div>
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-neutral-400 group-hover:text-primary-500 transition-smooth" />
        )}
      </div>
    </div>
  )

  if (step.isComplete) {
    return content
  }

  return (
    <OrgLink to={step.to} className="block hover:bg-primary-50/50 rounded-lg transition-smooth">
      {content}
    </OrgLink>
  )
}
